const DEFAULT_AVATAR_ZOOM = 25;
const MAX_AVATAR_EXPORT_SIZE = 512;

export function getAvatarInitials(name: string): string {
  const nameParts = name.trim().split(/\s+/).filter(Boolean);

  if (!nameParts.length) return "";
  if (nameParts.length === 1) return nameParts[0].slice(0, 2).toUpperCase();

  return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
}

function getAvatarScale(zoom: number) {
  return 1 + (zoom - DEFAULT_AVATAR_ZOOM) / 100;
}

function clampAvatarZoom(zoom: number) {
  return Math.min(100, Math.max(DEFAULT_AVATAR_ZOOM, zoom));
}

function extensionFromMimeType(type: string) {
  if (type === "image/jpeg") return "jpg";
  if (type === "image/png") return "png";
  if (type === "image/webp") return "webp";

  return "png";
}

function getAvatarFileName(type: string, sourceFile?: File | null) {
  if (sourceFile?.name) {
    return sourceFile.name;
  }

  return `avatar.${extensionFromMimeType(type)}`;
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load avatar image."));
    image.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Unable to prepare avatar image."));
        return;
      }

      resolve(blob);
    }, type);
  });
}

async function getSourceBlob({
  sourceFile,
  sourceUrl,
}: {
  sourceFile?: File | null;
  sourceUrl?: string;
}) {
  if (sourceFile) {
    return sourceFile;
  }

  if (!sourceUrl) {
    throw new Error("Avatar source is required.");
  }

  const response = await fetch(sourceUrl);
  if (!response.ok) {
    throw new Error("Unable to download avatar image.");
  }

  return response.blob();
}

export async function buildAvatarUploadFile({
  sourceFile,
  sourceUrl,
  zoom,
}: {
  sourceFile?: File | null;
  sourceUrl?: string;
  zoom: number;
}) {
  const sourceBlob = await getSourceBlob({ sourceFile, sourceUrl });
  const sourceType =
    sourceBlob.type && sourceBlob.type.startsWith("image/") ? sourceBlob.type : "image/png";
  const objectUrl = window.URL.createObjectURL(sourceBlob);

  try {
    const image = await loadImage(objectUrl);
    const squareSize = Math.min(image.naturalWidth, image.naturalHeight);
    const scale = getAvatarScale(clampAvatarZoom(zoom));
    const sourceCropSize = squareSize / scale;
    const offsetX = (image.naturalWidth - sourceCropSize) / 2;
    const offsetY = (image.naturalHeight - sourceCropSize) / 2;
    const outputSize = Math.min(MAX_AVATAR_EXPORT_SIZE, squareSize);
    const canvas = document.createElement("canvas");

    canvas.width = outputSize;
    canvas.height = outputSize;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to prepare avatar image.");
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(
      image,
      offsetX,
      offsetY,
      sourceCropSize,
      sourceCropSize,
      0,
      0,
      outputSize,
      outputSize
    );

    const blob = await canvasToBlob(canvas, sourceType);

    return new File([blob], getAvatarFileName(sourceType, sourceFile), {
      type: sourceType,
      lastModified: Date.now(),
    });
  } finally {
    if (typeof window.URL.revokeObjectURL === "function") {
      window.URL.revokeObjectURL(objectUrl);
    }
  }
}

export { DEFAULT_AVATAR_ZOOM };
